import React, { useEffect, useState } from 'react';
import {
  AmbienceSettingsForm,
  mapAgentAmbienceDraftToRequest,
  resolveAmbienceSettingsErrorMessage,
  showAmbienceSettingsErrorToast,
  showAmbienceSettingsSuccessToast,
  useAmbienceSettingsController,
} from '@features/Admin/AmbienceSettings';
import {
  AgentDto,
  CampaignDto,
  useBackgroundSoundAgentSettingsUpdateControllerExecuteMutation,
} from '@shared/services/apiService/apiService';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

type AgentAmbienceSettingsModalProps = {
  open: boolean;
  agent: AgentDto;
  campaignBackgroundSounds?: CampaignDto['backgroundSounds'] | null;
  isReadOnly: boolean;
  readOnlyReason?: string | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const AgentAmbienceSettingsModal: React.FC<AgentAmbienceSettingsModalProps> = ({
  open,
  agent,
  campaignBackgroundSounds,
  isReadOnly,
  readOnlyReason,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [updateAgentBackgroundSounds, { isLoading: isSaving }] =
    useBackgroundSoundAgentSettingsUpdateControllerExecuteMutation();
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const {
    sounds: backgroundSounds,
    isLoadingSounds,
    draft,
    setDraft,
    isDirty,
    hasValidationError,
    hasMissingSounds,
    ambientDisabled,
    thinkingDisabled,
    ambientNotice,
    thinkingNotice,
  } = useAmbienceSettingsController({
    mode: 'agent',
    backgroundSounds: agent.backgroundSounds,
    campaignBackgroundSounds,
    isReadOnly,
    enabled: open,
  });

  useEffect(() => {
    if (!open || (!hasValidationError && !hasMissingSounds)) {
      setHasAttemptedSave(false);
    }
  }, [hasMissingSounds, hasValidationError, open]);

  const handleSave = async () => {
    if (!draft || isReadOnly || isLoadingSounds || !isDirty) {
      return;
    }

    if (hasValidationError || hasMissingSounds) {
      setHasAttemptedSave(true);
      return;
    }

    try {
      await updateAgentBackgroundSounds({
        agentId: agent.id,
        agentBackgroundSoundsRequestDto: mapAgentAmbienceDraftToRequest(draft),
      }).unwrap();

      showAmbienceSettingsSuccessToast({
        title: t('common.saved'),
        description: t('adminMainPage.agentAmbienceSettings.saveSuccessDescription'),
      });

      await onSaved();
      onClose();
    } catch (error) {
      showAmbienceSettingsErrorToast(
        resolveAmbienceSettingsErrorMessage(
          error,
          t('adminMainPage.agentAmbienceSettings.saveErrorDescription'),
        ),
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('adminMainPage.agentAmbienceSettings.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {readOnlyReason ? (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              {readOnlyReason}
            </Typography>
          ) : null}

          <AmbienceSettingsForm
            isLoadingSounds={isLoadingSounds}
            sounds={backgroundSounds}
            value={draft}
            onChange={setDraft}
            ambientDisabled={ambientDisabled}
            thinkingDisabled={thinkingDisabled}
            ambientNotice={ambientNotice}
            thinkingNotice={thinkingNotice}
            showOverrideControls={false}
            rootSx={{ padding: 0 }}
            hasAttemptedSave={hasAttemptedSave}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>{t('adminMainPage.operation.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            isReadOnly ||
            isSaving ||
            isLoadingSounds ||
            !draft ||
            !isDirty ||
            hasValidationError ||
            hasMissingSounds
          }>
          {t('adminMainPage.operation.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentAmbienceSettingsModal;
